import { yupResolver } from '@hookform/resolvers/yup';
import { Trash2Icon } from 'lucide-react';
import { Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useCloseModal } from '@/hooks/useCloseModal';
import { useMacros } from '@/hooks/useMacros';
import { popModal, pushModal } from '@/redux/modals/actions';
import { LENGTH_1024, LENGTH_XS } from '@/util/validation/constants';

import { MODAL_CONFIRMATION } from '../ModalConductor';

type Props = { macroId?: string; initialName?: string };

const ModalCannedResponses = ({ macroId, initialName }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const closeModal = useCloseModal();
  const isEditing = !!macroId;

  const { macros, macro, addMacro, addStatus, updateMacro, updateStatus, deleteMacroAsync } =
    useMacros(macroId);

  const schema = yup.object({
    name: yup
      .string()
      .trim()
      .required(() => t('validation.required'))
      .min(2, () => t('validation.at_least', { 0: 2 }))
      .max(LENGTH_XS, () => t('validation.less_than', { 0: LENGTH_XS }))
      .test(
        'unique',
        () => t('chatBox.modal.title_already_exists'),
        (value) =>
          !macros?.some(
            (m) => m.name.trim().toLowerCase() === value?.toLowerCase() && m.macro_id !== macroId
          )
      ),
    text: yup
      .string()
      .required(() => t('validation.required'))
      .max(LENGTH_1024, () => t('validation.less_than', { 0: LENGTH_1024 })),
  });

  type FormValues = { name: string; text: string };

  const form = useForm<FormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema) as Resolver<FormValues>,
    values: { name: macro?.name ?? initialName ?? '', text: macro?.text ?? '' },
  });

  const onSubmit = (data: FormValues) => {
    if (isEditing) {
      updateMacro({ ...data, macro_id: macroId }, { onSuccess: () => dispatch(popModal()) });
    } else {
      addMacro(data, { onSuccess: () => dispatch(popModal()) });
    }
  };

  const handleDelete = () => {
    dispatch(
      pushModal(MODAL_CONFIRMATION, {
        title: t('chatBox.modal.delete_saved_response_title'),
        subtitle: t('chatBox.modal.delete_saved_response_subtitle', { 0: macro?.name }),
        primaryButtonText: t('common.delete'),
        danger: true,
        onConfirm: async () => {
          await deleteMacroAsync(macroId);
          // Pop both modals: confirmation + edit.
          dispatch(popModal());
          dispatch(popModal());
        },
      })
    );
  };

  const isSubmitting = isEditing ? updateStatus === 'pending' : addStatus === 'pending';

  return (
    <Dialog open onOpenChange={(open) => !open && closeModal()}>
      <DialogContent
        className="!rounded-lg sm:max-w-xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t('chatBox.modal.edit_quick_response')
              : t('chatBox.modal.create_quick_response')}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('chatBox.modal.title_label')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('chatBox.modal.title_placeholder')}
                      autoFocus={!initialName}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('chatBox.modal.response')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('chatBox.modal.response_placeholder')}
                      autoFocus={!!initialName}
                      className="min-h-32"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Form>

        <DialogFooter className="!pt-10">
          {isEditing && (
            <Button
              type="button"
              variant="destructive-ghost"
              className="mr-auto gap-2"
              onClick={handleDelete}
            >
              <Trash2Icon size={16} />
              {t('chatBox.modal.delete_response')}
            </Button>
          )}
          <Button variant="ghost-neutral" onClick={closeModal}>
            {t('chatBox.modal.cancel')}
          </Button>
          <Button onClick={form.handleSubmit(onSubmit)} isLoading={isSubmitting}>
            {isEditing ? t('chatBox.modal.update') : t('chatBox.modal.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalCannedResponses;
